import { z } from 'zod';
/**
 * Vercel AI SDK UI Message Stream part schemas.
 *
 * Wire and persistence format for assistant messages. The discriminator is the
 * `type` field; consumers should use {@link UIMessagePart} (a discriminated
 * union) so narrowing falls out of `switch (part.type)`.
 *
 * See `docs/response-formatting-design.md` §A (shared parts schema), §G
 * (reasoning lifecycle), §H (message persistence shape).
 */
const isoDateTime = z.string().datetime({ offset: true });
export const TextPart = z
    .object({
    type: z.literal('text'),
    text: z.string(),
})
    .strict();
/**
 * Reasoning is one logical part with multiple deltas plus a finish-time
 * finalization. `supersededAtFinish=true` instructs the renderer to collapse
 * preliminaries behind a short summary (verbose deltas remain inspectable).
 */
export const ReasoningPart = z
    .object({
    type: z.literal('reasoning'),
    text: z.string(),
    supersededAtFinish: z.boolean().optional(),
    finalSummary: z.string().optional(),
})
    .strict();
export const ToolCallPart = z
    .object({
    type: z.literal('tool-call'),
    toolCallId: z.string().min(1),
    toolName: z.string().min(1),
    input: z.unknown(),
})
    .strict();
export const ToolResultPart = z
    .object({
    type: z.literal('tool-result'),
    toolCallId: z.string().min(1),
    toolName: z.string().min(1),
    output: z.unknown(),
    isError: z.boolean().optional(),
})
    .strict();
const StructuredGiven = z
    .object({
    label: z.string(),
    value: z.string(),
    unit: z.string().optional(),
})
    .strict();
const StructuredStep = z
    .object({
    title: z.string(),
    explanation: z.string(),
    expression: z.string().optional(),
})
    .strict();
const StructuredFinalAnswer = z
    .object({
    value: z.string(),
    unit: z.string().optional(),
})
    .strict();
export const StructuredResponsePart = z
    .object({
    type: z.literal('data-structured-response'),
    problem: z.string(),
    given: z.array(StructuredGiven),
    steps: z.array(StructuredStep),
    finalAnswer: StructuredFinalAnswer,
})
    .strict();
const TimeseriesStats = z
    .object({
    min: z.number(),
    max: z.number(),
    mean: z.number(),
    p50: z.number(),
    p95: z.number(),
})
    .strict();
const TimeseriesSample = z
    .object({
    t: isoDateTime,
    v: z.number(),
})
    .strict();
export const TimeseriesPart = z
    .object({
    type: z.literal('data-timeseries'),
    tagId: z.string().min(1),
    range: z
        .object({
        start: isoDateTime,
        end: isoDateTime,
    })
        .strict(),
    stats: TimeseriesStats,
    manifestGaps: z.array(z.string()).default([]),
    series: z.array(TimeseriesSample).optional(),
})
    .strict();
export const CitationPart = z
    .object({
    type: z.literal('data-citation'),
    label: z.string().min(1),
    href: z.string().min(1),
    sourceType: z.enum(['document', 'path', 'equipment', 'sheet', 'tag']),
    snippet: z.string().optional(),
})
    .strict();
/**
 * `data-chart` carries a higher-level (tag, range, stats) shape that the
 * renderer compiles to the visualization layer (Vega-Lite or otherwise).
 * Keeping the model out of the visualization layer is the open-question
 * resolution favored in the design doc.
 */
export const ChartPart = z
    .object({
    type: z.literal('data-chart'),
    tagId: z.string().min(1),
    range: z
        .object({
        start: isoDateTime,
        end: isoDateTime,
    })
        .strict(),
    chartType: z.enum(['line', 'bar', 'area']),
    stats: TimeseriesStats.optional(),
})
    .strict();
export const WorkflowPhaseStatus = z.enum([
    'pending',
    'running',
    'completed',
    'waiting',
    'blocked',
    'failed',
    'skipped',
]);
export const WorkflowProgressTool = z
    .object({
    id: z.string().min(1),
    name: z.string().min(1),
    status: z.enum(['running', 'ok', 'error']),
    inputPreview: z.string().optional(),
    outputPreview: z.string().optional(),
    errorPreview: z.string().optional(),
})
    .strict();
export const WorkflowProgressPhase = z
    .object({
    id: z.string().min(1),
    title: z.string().min(1),
    status: WorkflowPhaseStatus,
    summary: z.string().optional(),
    evidenceCount: z.number().int().nonnegative().optional(),
    toolCallCount: z.number().int().nonnegative().optional(),
    tools: z.array(WorkflowProgressTool).optional(),
})
    .strict();
export const WorkflowProgressPart = z
    .object({
    type: z.literal('data-workflow-progress'),
    workflowRunId: z.string().min(1),
    status: z.string().min(1),
    currentPhaseId: z.string().min(1).optional(),
    updatedAt: isoDateTime,
    phases: z.array(WorkflowProgressPhase).min(1),
})
    .strict();
/**
 * FS-663: a share the agent has PREPARED but NOT sent.
 *
 * The `share_with` tool cannot send — there is no mid-turn pause primitive on
 * the native chat path, so a model-set confirm flag would not be a confirmation
 * (see docs/egress-consent-design.md and share-with.tool.ts). This part carries
 * the resolved recipient and link to the client, which renders a card the HUMAN
 * confirms; Send then calls the same `POST /v1/shares` the share dialog uses.
 *
 * The recipient's EMAIL is carried, not just their name: "the wrong Ammar" has
 * to be visible at the moment the user decides.
 */
export const PendingSharePart = z
    .object({
    type: z.literal('data-pending-share'),
    resourceType: z.enum(['workspace', 'conversation']),
    resourceId: z.string().min(1),
    resourceName: z.string().min(1),
    url: z.string().min(1),
    recipient: z
        .object({
        userId: z.string().min(1),
        name: z.string().nullable(),
        email: z.string().min(1),
        picture: z.string().nullable(),
    })
        .strict(),
    note: z.string().optional(),
})
    .strict();
export const UIMessagePart = z.discriminatedUnion('type', [
    TextPart,
    ReasoningPart,
    ToolCallPart,
    ToolResultPart,
    StructuredResponsePart,
    TimeseriesPart,
    CitationPart,
    ChartPart,
    WorkflowProgressPart,
    PendingSharePart,
]);
export const PartTypes = [
    'text',
    'reasoning',
    'tool-call',
    'tool-result',
    'data-structured-response',
    'data-timeseries',
    'data-citation',
    'data-chart',
    'data-workflow-progress',
    'data-pending-share',
];
/**
 * Persisted shape for an assistant message. Duration is computed on read as
 * `completedAt - startedAt` and is never persisted (§H). Legacy rows with no
 * `completedAt` render without a duration stamp.
 */
export const AssistantMessage = z
    .object({
    id: z.string().min(1),
    conversationId: z.string().min(1),
    role: z.literal('assistant'),
    modelId: z.string().min(1),
    startedAt: isoDateTime,
    completedAt: isoDateTime.nullable(),
    parts: z.array(UIMessagePart),
})
    .strict();
export const PARTS_PROTOCOL_VERSION = 'v1';
